import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { TradingContext } from '../contexts/TradingContext';
import { getDashboardRouteForTab, getMarketTabRoute } from '../utils/dashboardMobileNav';
import { DASHBOARD_ROUTES } from '../constants/dashboardRoutes';
import { getTradeRoute } from '../components/DashboardSidebar/dashboardNavConfig';
import { canAccessMarketDataTools } from '../utils/brokerConnection';

/**
 * Bottom nav from DashboardShell → /dashboard/* routes.
 * Trade tab uses getTradeRoute (/trade). Trade and Bracket tabs are Pro-only on Essentials.
 * From standalone pages (e.g. /settings) → dashboard tab routes (or /trade for Trade).
 *
 * Watchlists / Portfolio / AI / etc. always use their dashboard routes — never
 * `navigate('/trade', { state: { page } })`, which Trade treats as a cold load and
 * redirects to Portfolio.
 */
export function useMobileBottomNavNavigate() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const canUseMarketTools = canAccessMarketDataTools(user, selectedAccount);

  return useCallback(
    (page, options = {}) => {
      if (page === 'trade') {
        navigate(getTradeRoute(user), { state: { page: 'trade' }, ...options });
        return;
      }
      if (page === 'health') {
        navigate(DASHBOARD_ROUTES.health, options);
        return;
      }
      if (page === 'market') {
        navigate(getMarketTabRoute(canUseMarketTools), options);
        return;
      }
      const route = getDashboardRouteForTab(page);
      if (route) {
        navigate(route, options);
        return;
      }
      // Legacy fallback for unknown in-page tabs only.
      navigate('/trade', { state: { page }, ...options });
    },
    [canUseMarketTools, navigate, user]
  );
}
